// Day/night sky (LIT-6, client): a background+fog colour that tracks the sun's
// height, plus sun/moon quads and a star field that fades in at night. Driven each
// frame by the sim's `SkyState` (from `world.skyState()`) — the sim owns time and
// sun/moon position (generic cycle, or real solar for Atlas worlds, SOL-1); this
// only renders it. The per-vertex daylight scale for terrain lives in the chunk
// shader (renderer.ts).
import * as THREE from 'three';
import { VIEW, WORLD } from '../../sim/config';
import type { SkyState } from '../../sim/atlas/solar';

const DAY_SKY = new THREE.Color(0x9ad0ec);
const NIGHT_SKY = new THREE.Color(0x05070f);
const DUSK_TINT = new THREE.Color(0xf28a3c); // warm horizon glow at dawn/dusk
const DAY_FOG = new THREE.Color(0x9ad0ec);
const NIGHT_FOG = new THREE.Color(0x090b14);
// The nether: a fixed dim-red gloom with no sun/moon/stars and closer fog.
const NETHER_SKY = new THREE.Color(0x2a0d0d);
const NETHER_FOG = new THREE.Color(0x330a0a);
// The End: a starless violet-black void (DIM-3).
const END_SKY = new THREE.Color(0x0a0713);
const END_FOG = new THREE.Color(0x14102a);

const SKY_RADIUS = 400; // sun/moon/star distance (inside camera far plane)

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/** Radial disc texture (sun/moon), procedural so there are no image assets. */
function discTexture(inner: string, outer: string): THREE.Texture {
  const cvs = document.createElement('canvas');
  cvs.width = cvs.height = 64;
  const ctx = cvs.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
  g.addColorStop(0, inner);
  g.addColorStop(0.7, outer);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(cvs);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export interface Sky {
  update(sky: SkyState, camera: THREE.Camera): void;
  /** Switch the sky between dimensions (DIM-1/DIM-3): the nether is a fixed red
   *  gloom, the End a starless violet-black void. Both are sunless. */
  setDimension(dim: 'overworld' | 'nether' | 'end'): void;
  /** Track the live render distance (UI-6): the fog wall must recede as the player
   *  raises render distance, or the extra meshed chunks stay hidden behind it. */
  setViewDistance(blocks: number): void;
}

export function applySky(scene: THREE.Scene): Sky {
  scene.background = DAY_SKY.clone();
  let far = VIEW.RENDER_DIST_DEFAULT * WORLD.SECTION;
  const fog = new THREE.Fog(DAY_FOG.clone(), far * 0.55, far * 0.95);
  scene.fog = fog;
  let dimension: 'overworld' | 'nether' | 'end' = 'overworld';
  // Re-derive the fog band from the current view distance + dimension.
  const applyFog = (): void => {
    if (dimension === 'nether') { fog.near = far * 0.15; fog.far = far * 0.7; }
    else if (dimension === 'end') { fog.near = far * 0.5; fog.far = far * 0.95; }
    else { fog.near = far * 0.55; fog.far = far * 0.95; }
  };

  // A backdrop group that always renders behind the world (no depth interaction).
  const backdrop = new THREE.Group();
  scene.add(backdrop);

  const celestial = (tex: THREE.Texture, size: number): THREE.Mesh => {
    // depthTest stays on so terrain (opaque, drawn before any transparent pass
    // regardless of renderOrder) correctly occludes the sun/moon when behind it;
    // depthWrite stays off so the disc's soft translucent rim doesn't wrongly
    // occlude stars/other backdrop elements behind it.
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: true, depthWrite: false, fog: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
    mesh.renderOrder = -10;
    backdrop.add(mesh);
    return mesh;
  };
  const sun = celestial(discTexture('rgba(255,248,220,1)', 'rgba(255,220,130,0.9)'), 60);
  const moon = celestial(discTexture('rgba(232,238,255,1)', 'rgba(180,196,230,0.7)'), 40);

  // Star field on a sphere shell — opacity ramps up at night.
  const starGeo = new THREE.BufferGeometry();
  const STAR_COUNT = 900;
  const pos = new Float32Array(STAR_COUNT * 3);
  for (let i = 0; i < STAR_COUNT; i++) {
    // Uniform on a sphere; keep them above the horizon-ish for a night-sky look.
    const u = Math.random() * 2 - 1;
    const t = Math.random() * Math.PI * 2;
    const r = Math.sqrt(1 - u * u);
    pos[i * 3] = Math.cos(t) * r * SKY_RADIUS;
    pos[i * 3 + 1] = Math.abs(u) * SKY_RADIUS; // upper hemisphere
    pos[i * 3 + 2] = Math.sin(t) * r * SKY_RADIUS;
  }
  starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 1.6, sizeAttenuation: false, transparent: true, opacity: 0, depthTest: true, depthWrite: false, fog: false });
  const stars = new THREE.Points(starGeo, starMat);
  stars.renderOrder = -11;
  backdrop.add(stars);

  const scratch = new THREE.Vector3();

  return {
    setViewDistance(blocks): void {
      far = Math.max(WORLD.SECTION, blocks);
      applyFog();
    },
    setDimension(dim): void {
      dimension = dim;
      if (dim === 'nether') {
        (scene.background as THREE.Color).copy(NETHER_SKY);
        fog.color.copy(NETHER_FOG);
        backdrop.visible = false; // no sun/moon/stars in the nether
      } else if (dim === 'end') {
        (scene.background as THREE.Color).copy(END_SKY);
        fog.color.copy(END_FOG);
        backdrop.visible = false; // the End has no sun, moon or stars either
      } else {
        backdrop.visible = true;
      }
      applyFog();
    },
    update(sky, camera): void {
      if (dimension === 'nether') {
        (scene.background as THREE.Color).copy(NETHER_SKY);
        fog.color.copy(NETHER_FOG);
        return; // fixed gloom — no day/night, sun, moon, or stars
      }
      if (dimension === 'end') {
        (scene.background as THREE.Color).copy(END_SKY);
        fog.color.copy(END_FOG);
        return; // fixed void — no day/night cycle in the End
      }
      // The sim places the sun/moon (generic east–west circle, or real solar for an
      // Atlas world). Colour + opacities key off the sun's height (dir.y).
      const sd = sky.sun.dir, md = sky.moon.dir;
      const sunY = sd.y; // −1 (below) … +1 (zenith)
      const dayness = smoothstep(-0.12, 0.22, sunY); // 0 night → 1 day
      const horizon = clamp01(1 - Math.abs(sunY) * 3.2); // warm band near sunrise/set

      const bg = NIGHT_SKY.clone().lerp(DAY_SKY, dayness).lerp(DUSK_TINT, horizon * 0.5);
      (scene.background as THREE.Color).copy(bg);
      fog.color.copy(NIGHT_FOG.clone().lerp(DAY_FOG, dayness).lerp(DUSK_TINT, horizon * 0.45));

      // Keep the backdrop centred on the camera so it sits at infinity.
      camera.getWorldPosition(scratch);
      backdrop.position.copy(scratch);
      sun.position.set(scratch.x + sd.x * SKY_RADIUS, scratch.y + sd.y * SKY_RADIUS, scratch.z + sd.z * SKY_RADIUS);
      moon.position.set(scratch.x + md.x * SKY_RADIUS, scratch.y + md.y * SKY_RADIUS, scratch.z + md.z * SKY_RADIUS);
      sun.lookAt(scratch);
      moon.lookAt(scratch);
      (sun.material as THREE.MeshBasicMaterial).opacity = clamp01(dayness * 1.2 + horizon * 0.5);
      (moon.material as THREE.MeshBasicMaterial).opacity = clamp01(1 - dayness);
      starMat.opacity = clamp01(1 - dayness * 1.4);
    },
  };
}
